import { formatTime, getDateLocale } from '../../../lib/base-i18n';
import { tCalendar } from '../../../lib/calendar-i18n';
import { inclusiveEndDayNumber, localDayNumber } from '../recurrence-dates';

/**
 * 일정 상세의 «일시» 글자 (2026-10-19 사용자 지적 「2일 이상인 일정인데 하루만 나오고 종료 시간만 나온다」).
 *
 * 종전: 시작 «날짜» 하나 + 「시작 시각 – 종료 시각」 ⇒ 여러 날 일정이면 종료 «날짜»가 사라졌다.
 * 이제:
 * ```
 * 하루 일정        2026년 10월 26일 (월)          /  오전 10:00 – 오후 3:00     (종전 그대로)
 * 하루종일(하루)   2026년 10월 26일 (월)          /  종일                      (종전 그대로)
 * 여러 날 시각     2026년 10월 26일 (월) 오전 10:00
 *                  ~ 10월 28일 (수) 오후 3:00                     (아랫줄 없음)
 * 여러 날 종일     2026년 10월 26일 (월)
 *                  ~ 10월 28일 (수)               /  종일
 * ⭐ 2026-10-19 사용자 지시 — 종료 줄은 시작과 «같은 해»면 연도를 뺀다(해가 바뀌면 그대로 적는다).
 *   「n일 일정」·「· n일」 같은 날수 글자는 없앴다.
 * ```
 * ⚠ 날짜 셈은 월간 배치·여러 날 채움과 같다 — 종료가 자정 정각이면 그 날은 포함하지 않는다
 *   (하루종일 일정은 23:59 로 끝나므로 그 날이 마지막 날이다).
 */
export interface EventSpanLabel {
  /** 굵은 줄(1~2줄). */
  lines: string[];
  /** 그 아래 옅은 줄 — 빈 글자면 그리지 않는다. */
  secondary: string;
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' };

export function eventSpanLabel(startAt: string | Date, endAt: string | Date, isAllDay: boolean): EventSpanLabel {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const locale = getDateLocale();
  const dateOf = (d: Date) => d.toLocaleDateString(locale, DATE_OPTS);
  /** 종료 줄 — 시작과 같은 해면 연도를 뺀다. */
  const endDateOf = (d: Date) => (d.getFullYear() === start.getFullYear()
    ? d.toLocaleDateString(locale, { month: 'long', day: 'numeric', weekday: 'short' })
    : dateOf(d));

  // 자정 정각 종료는 그 전날까지 — 「10/26 10시 ~ 10/27 0시」는 하루 일정이다.
  const startDay = localDayNumber(start);
  const endDay = inclusiveEndDayNumber(start, end);
  const days = Math.max(1, endDay - startDay + 1);

  if (days === 1) {
    return {
      lines: [dateOf(start)],
      secondary: isAllDay ? tCalendar('calendar.detail.all_day') : `${formatTime(start)} – ${formatTime(end)}`,
    };
  }
  const lastDay = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (days - 1));
  if (isAllDay) {
    return {
      lines: [dateOf(start), `~ ${endDateOf(lastDay)}`],
      secondary: tCalendar('calendar.detail.all_day'),
    };
  }
  return {
    lines: [`${dateOf(start)} ${formatTime(start)}`, `~ ${endDateOf(end)} ${formatTime(end)}`],
    secondary: '',
  };
}
